import { UserRole } from "../types/UserRole";
import {
  getStatusItemsBySpreadsheet,
  updateStatusItemStatus,
  StatusItemDto,
} from "./status-items.service";
import {
  getDiscussionsBySpreadsheet,
  DiscussionDto,
} from "./discussions.service";
import { ExcelService } from "./excel.service";

/**
 * Result of a bulk review operation
 */
export interface BulkReviewResult {
  statusItemsUpdated: number;
  statusItemsFailed: number;
  discussionsUpdated: number;
  discussionsFailed: number;
  errors: string[];
}

/**
 * Get the terminal status for a status item based on user role
 * - Preparer: "Processed"
 * - Reviewer: "Quality Reviewed"
 * - Partner: "Partner Reviewed"
 */
export function getStatusItemTerminalStatus(userRole: UserRole): string | null {
  switch (userRole) {
    case UserRole.Preparer:
      return "Processed";
    case UserRole.Reviewer:
      return "Quality Reviewed";
    case UserRole.Partner:
      return "Partner Reviewed";
    case UserRole.User:
    default:
      // Users cannot change status
      return null;
  }
}

/**
 * Get the terminal status for a discussion based on user role and discussion type
 * - Preparer: "Processed" (cannot fully close/resolve)
 * - Reviewer/Partner: "Resolved" for review points, "Closed" for everything else
 */
export function getDiscussionTerminalStatus(
  userRole: UserRole,
  discussionType: string
): string | null {
  const isReviewPoint = discussionType === "review_point";

  switch (userRole) {
    case UserRole.Preparer:
      // Preparers can only set to Processed
      return "Processed";
    case UserRole.Reviewer:
    case UserRole.Partner:
      // Reviewers and Partners can fully close/resolve
      return isReviewPoint ? "Resolved" : "Closed";
    case UserRole.User:
    default:
      // Users cannot change status
      return null;
  }
}

/**
 * Check if a status item needs to be updated (not already at or past terminal status)
 */
function statusItemNeedsUpdate(
  currentStatus: string | undefined,
  terminalStatus: string,
  _userRole: UserRole
): boolean {
  if (!currentStatus) return true;

  const normalizedCurrent = currentStatus.toLowerCase();

  // Status progression order
  const statusOrder = ["open", "processed", "rework required", "quality reviewed", "partner reviewed"];
  const currentIndex = statusOrder.indexOf(normalizedCurrent);
  const terminalIndex = statusOrder.indexOf(terminalStatus.toLowerCase());

  // If current status is at or past the terminal status for this role, no update needed
  if (currentIndex >= terminalIndex) {
    return false;
  }

  return true;
}

/**
 * Check if a discussion needs to be updated (not already at terminal status)
 */
function discussionNeedsUpdate(
  currentStatus: string | undefined,
  terminalStatus: string
): boolean {
  if (!currentStatus) return true;

  const normalizedCurrent = currentStatus.toLowerCase();
  const normalizedTerminal = terminalStatus.toLowerCase();

  // If already at terminal status, no update needed
  return normalizedCurrent !== normalizedTerminal;
}

/**
 * Perform a bulk review operation on a specific sheet
 * Updates all status items on the sheet and their linked discussions to their terminal states
 * based on the user's role.
 *
 * @param spreadsheetId - The spreadsheet ID
 * @param sheetName - The sheet name to review
 * @param userRole - The user's role (determines terminal statuses)
 * @returns Result containing counts of updated items and any errors
 */
export async function bulkReviewSheet(
  spreadsheetId: string,
  sheetName: string,
  userRole: UserRole
): Promise<BulkReviewResult> {
  const result: BulkReviewResult = {
    statusItemsUpdated: 0,
    statusItemsFailed: 0,
    discussionsUpdated: 0,
    discussionsFailed: 0,
    errors: [],
  };

  // Get terminal statuses for this role
  const statusItemTerminal = getStatusItemTerminalStatus(userRole);
  if (!statusItemTerminal) {
    result.errors.push("Your role does not have permission to perform bulk review operations. You can change your role in the Settings menu.");
    return result;
  }

  try {
    // Get the sheet ID from the sheet name
    const worksheets = await ExcelService.getAllWorksheetsInfo();
    const worksheet = worksheets.find(
      (ws) => ws.name.toLowerCase() === sheetName.toLowerCase()
    );

    if (!worksheet) {
      result.errors.push(`Sheet "${sheetName}" not found.`);
      return result;
    }

    const sheetId = worksheet.id;

    // Get all status items for the spreadsheet
    const allStatusItems = await getStatusItemsBySpreadsheet(spreadsheetId);

    // Filter to only items on the specified sheet (by ID)
    const sheetStatusItems = allStatusItems.filter(
      (item) => item.sheetId === sheetId
    );

    if (sheetStatusItems.length === 0) {
      result.errors.push(`No status items found on sheet "${sheetName}".`);
      return result;
    }

    // Update status items only — discussions are surfaced in the preview for
    // the reviewer to acknowledge but are not auto-closed here. Reviewers must
    // close discussions individually so they can review the contents first.
    for (const statusItem of sheetStatusItems) {
      if (!statusItemNeedsUpdate(statusItem.status, statusItemTerminal, userRole)) {
        // Already at or past terminal status, skip
        continue;
      }

      try {
        await updateStatusItemStatus(statusItem.id, statusItemTerminal);
        result.statusItemsUpdated++;
      } catch (error) {
        result.statusItemsFailed++;
        result.errors.push(`Failed to update status item "${statusItem.name}": ${error}`);
      }
    }

    return result;
  } catch (error) {
    result.errors.push(`Failed to perform bulk review: ${error}`);
    return result;
  }
}

/**
 * Preview what a bulk review operation would do without actually performing it
 *
 * @param spreadsheetId - The spreadsheet ID
 * @param sheetName - The sheet name to review
 * @param userRole - The user's role (determines terminal statuses)
 * @returns Preview of what would be updated
 */
export interface BulkReviewPreview {
  statusItemsToUpdate: StatusItemDto[];
  /**
   * Discussions linked to this sheet's status items that are still open (not at
   * a terminal status for the user's role). Listed for the reviewer to
   * acknowledge before sign-off — bulk review does NOT close them.
   */
  openDiscussionsOnSheet: DiscussionDto[];
  statusItemTerminalStatus: string | null;
  canPerformReview: boolean;
  message?: string;
}

/**
 * Aggregated preview for signing off multiple sheets at once.
 */
export interface MultiSheetBulkReviewPreview {
  perSheet: Array<{
    sheetName: string;
    sheetId: string;
    statusItemsToUpdate: StatusItemDto[];
    openDiscussions: DiscussionDto[];
  }>;
  /** Total status items needing update across all selected sheets. */
  totalStatusItemsToUpdate: number;
  /** Discussions across all selected sheets, deduped by id. */
  openDiscussionsAggregated: DiscussionDto[];
  statusItemTerminalStatus: string | null;
  canPerformReview: boolean;
  message?: string;
}

/**
 * Preview a multi-sheet sign-off. Fetches status items / discussions / worksheet
 * metadata once and slices them per selected sheet, so toggling sheets in the
 * picker doesn't trigger N round-trips.
 */
export async function previewBulkReviewMultiple(
  spreadsheetId: string,
  sheetNames: string[],
  userRole: UserRole
): Promise<MultiSheetBulkReviewPreview> {
  const statusItemTerminal = getStatusItemTerminalStatus(userRole);

  if (!statusItemTerminal) {
    return {
      perSheet: [],
      totalStatusItemsToUpdate: 0,
      openDiscussionsAggregated: [],
      statusItemTerminalStatus: null,
      canPerformReview: false,
      message: "Your role does not have permission to perform bulk review operations. You can change your role in the Settings menu.",
    };
  }

  if (sheetNames.length === 0) {
    return {
      perSheet: [],
      totalStatusItemsToUpdate: 0,
      openDiscussionsAggregated: [],
      statusItemTerminalStatus: statusItemTerminal,
      canPerformReview: false,
      message: "Select at least one sheet to sign off.",
    };
  }

  try {
    const [worksheets, allStatusItems, allDiscussions] = await Promise.all([
      ExcelService.getAllWorksheetsInfo(),
      getStatusItemsBySpreadsheet(spreadsheetId),
      getDiscussionsBySpreadsheet(spreadsheetId),
    ]);

    const lowerNames = new Set(sheetNames.map((n) => n.toLowerCase()));
    const targetWorksheets = worksheets.filter((w) => lowerNames.has(w.name.toLowerCase()));

    const perSheet = targetWorksheets.map((worksheet) => {
      const sheetStatusItemsAll = allStatusItems.filter((item) => item.sheetId === worksheet.id);
      const sheetStatusItemsToUpdate = sheetStatusItemsAll.filter((item) =>
        statusItemNeedsUpdate(item.status, statusItemTerminal, userRole)
      );
      const sheetStatusItemIds = new Set(sheetStatusItemsAll.map((item) => item.id));

      const linkedDiscussions = allDiscussions.filter((discussion) => {
        if (!discussion.statusItemIds?.some((id) => sheetStatusItemIds.has(id))) return false;
        const terminal = getDiscussionTerminalStatus(userRole, discussion.type);
        return terminal && discussionNeedsUpdate(discussion.status, terminal);
      });

      return {
        sheetName: worksheet.name,
        sheetId: worksheet.id,
        statusItemsToUpdate: sheetStatusItemsToUpdate,
        openDiscussions: linkedDiscussions,
      };
    });

    const totalStatusItemsToUpdate = perSheet.reduce((acc, s) => acc + s.statusItemsToUpdate.length, 0);
    const aggregatedById = new Map<number, DiscussionDto>();
    for (const sheet of perSheet) {
      for (const d of sheet.openDiscussions) aggregatedById.set(d.id, d);
    }

    return {
      perSheet,
      totalStatusItemsToUpdate,
      openDiscussionsAggregated: Array.from(aggregatedById.values()),
      statusItemTerminalStatus: statusItemTerminal,
      canPerformReview: true,
    };
  } catch (error) {
    return {
      perSheet: [],
      totalStatusItemsToUpdate: 0,
      openDiscussionsAggregated: [],
      statusItemTerminalStatus: statusItemTerminal,
      canPerformReview: false,
      message: `Failed to preview bulk review: ${error}`,
    };
  }
}

/**
 * Execute bulk review across multiple sheets. Loops the existing per-sheet
 * implementation and merges counts/errors so callers see a single result.
 */
export async function bulkReviewMultipleSheets(
  spreadsheetId: string,
  sheetNames: string[],
  userRole: UserRole
): Promise<BulkReviewResult> {
  const merged: BulkReviewResult = {
    statusItemsUpdated: 0,
    statusItemsFailed: 0,
    discussionsUpdated: 0,
    discussionsFailed: 0,
    errors: [],
  };

  for (const sheetName of sheetNames) {
    const r = await bulkReviewSheet(spreadsheetId, sheetName, userRole);
    merged.statusItemsUpdated += r.statusItemsUpdated;
    merged.statusItemsFailed += r.statusItemsFailed;
    merged.discussionsUpdated += r.discussionsUpdated;
    merged.discussionsFailed += r.discussionsFailed;
    merged.errors.push(...r.errors);
  }

  return merged;
}

export async function previewBulkReview(
  spreadsheetId: string,
  sheetName: string,
  userRole: UserRole
): Promise<BulkReviewPreview> {
  const statusItemTerminal = getStatusItemTerminalStatus(userRole);

  if (!statusItemTerminal) {
    return {
      statusItemsToUpdate: [],
      openDiscussionsOnSheet: [],
      statusItemTerminalStatus: null,
      canPerformReview: false,
      message: "Your role does not have permission to perform bulk review operations. You can change your role in the Settings menu.",
    };
  }

  try {
    // Get the sheet ID from the sheet name
    const worksheets = await ExcelService.getAllWorksheetsInfo();
    const worksheet = worksheets.find(
      (ws) => ws.name.toLowerCase() === sheetName.toLowerCase()
    );

    if (!worksheet) {
      return {
        statusItemsToUpdate: [],
        openDiscussionsOnSheet: [],
        statusItemTerminalStatus: statusItemTerminal,
        canPerformReview: false,
        message: `Sheet "${sheetName}" not found.`,
      };
    }

    const sheetId = worksheet.id;

    // Get all status items for the spreadsheet
    const allStatusItems = await getStatusItemsBySpreadsheet(spreadsheetId);

    // Filter to only items on the specified sheet that need updating
    const sheetStatusItems = allStatusItems.filter(
      (item) =>
        item.sheetId === sheetId &&
        statusItemNeedsUpdate(item.status, statusItemTerminal, userRole)
    );

    // Get all discussions for the spreadsheet
    const allDiscussions = await getDiscussionsBySpreadsheet(spreadsheetId);

    // Build a set of status item IDs on this sheet
    const sheetStatusItemIds = new Set(
      allStatusItems
        .filter((item) => item.sheetId === sheetId)
        .map((item) => item.id)
    );

    // Find discussions linked to status items on this sheet that need updating
    const linkedDiscussions = allDiscussions.filter((discussion) => {
      if (!discussion.statusItemIds?.some((id) => sheetStatusItemIds.has(id))) {
        return false;
      }
      const discussionTerminal = getDiscussionTerminalStatus(userRole, discussion.type);
      return discussionTerminal && discussionNeedsUpdate(discussion.status, discussionTerminal);
    });

    return {
      statusItemsToUpdate: sheetStatusItems,
      openDiscussionsOnSheet: linkedDiscussions,
      statusItemTerminalStatus: statusItemTerminal,
      canPerformReview: true,
    };
  } catch (error) {
    return {
      statusItemsToUpdate: [],
      openDiscussionsOnSheet: [],
      statusItemTerminalStatus: statusItemTerminal,
      canPerformReview: false,
      message: `Failed to preview bulk review: ${error}`,
    };
  }
}
